/**
 * PasswordStrengthMeter — a lightweight rule-based strength indicator.
 *
 * Renders NOTHING until the user starts typing (so empty fields stay clean).
 * Score 0..4 derived from length + character-class variety; we intentionally
 * skip an external library (zxcvbn is ~500KB) because that's overkill for a
 * planner app. The rules below map cleanly onto common password guidance:
 *   0: Too short (< 8) — blocks submission (backend enforces too)
 *   1: Weak         (8+, limited variety)
 *   2: Fair         (length + some variety)
 *   3: Good         (length + good variety)
 *   4: Strong       (long + full variety)
 *
 * Exports a pure `scorePassword(pw)` helper so the submit button can wire in
 * a gate if ever needed (currently we just show feedback — the 8-char min is
 * still what guards submission everywhere).
 */
import React, { useMemo } from 'react';

const LEVELS = [
  { label: 'Too short', tone: 'text-muted', bar: 'bg-border', width: 'w-0' },
  { label: 'Weak',      tone: 'text-terracotta',    bar: 'bg-terracotta',    width: 'w-1/4' },
  { label: 'Fair',      tone: 'text-ochre',         bar: 'bg-ochre',         width: 'w-2/4' },
  { label: 'Good',      tone: 'text-moss',          bar: 'bg-moss',          width: 'w-3/4' },
  { label: 'Strong',    tone: 'text-moss',          bar: 'bg-moss',          width: 'w-full' },
];

export function scorePassword(pw) {
  if (!pw) return 0;
  if (pw.length < 8) return 0;
  let variety = 0;
  if (/[a-z]/.test(pw)) variety += 1;
  if (/[A-Z]/.test(pw)) variety += 1;
  if (/[0-9]/.test(pw)) variety += 1;
  if (/[^A-Za-z0-9]/.test(pw)) variety += 1;
  // Length bonus on top of variety — 12+ chars bumps you one level up.
  if (pw.length >= 12) variety += 1;
  // Clamp to 1..4 since we already returned 0 for < 8 chars.
  return Math.max(1, Math.min(4, variety));
}

export default function PasswordStrengthMeter({ password, 'data-testid': testid }) {
  const score = useMemo(() => scorePassword(password), [password]);
  if (!password) return null;
  const level = LEVELS[score];

  const tooShort = password.length < 8;
  const checks = [
    { ok: password.length >= 8,          text: 'At least 8 characters' },
    { ok: /[A-Z]/.test(password),        text: 'One uppercase letter' },
    { ok: /[0-9]/.test(password),        text: 'One number' },
    { ok: /[^A-Za-z0-9]/.test(password), text: 'One symbol' },
  ];

  return (
    <div className="mt-2 space-y-2" data-testid={testid || 'password-strength'}>
      <div className="flex items-center gap-3">
        <div className="flex-1 h-1 bg-border rounded-full overflow-hidden">
          <div
            className={`h-full ${level.bar} ${level.width} transition-all duration-300`}
            aria-hidden
          />
        </div>
        <span
          className={`font-mono text-[10px] uppercase tracking-widest ${level.tone}`}
          data-testid={testid ? `${testid}-label` : 'password-strength-label'}
        >
          {level.label}
        </span>
      </div>
      {tooShort && (
        <ul className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-muted">
          {checks.map((c) => (
            <li key={c.text} className={c.ok ? 'text-moss' : 'text-muted'}>
              <span className="mr-1">{c.ok ? '✓' : '○'}</span>
              {c.text}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
